import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import axios from 'axios';

// Types
import type { Saga } from 'redux-saga';

// Constants
import {
    WORK_TYPE_CREATE_REQUESTED,
    WORK_TYPE_DELETE_REQUESTED
} from './constants';

// Actions
import { enqueueSnackbar } from '../notifications/actions';
import { updateQueryString } from '../router/actions';
import {
    succeededCreateWorkType,
    failedCreateWorkType,
    succeededDeleteWorkType,
    failedDeleteWorkType,
    setWorkType
} from './actions';

// Selectors
import { idTokenSelector } from '../auth/selectors';

// Environment
const { REACT_APP_API_URL } = process.env;

type Action = {
    +type: string,
    +payload: Object
};

export function* createWorkType(action: Action): Saga<void> {
    const {
        payload: { data }
    } = action;

    // Select ID Token
    const idToken = yield select(idTokenSelector);

    try {
        const response = yield call(axios, {
            method: 'post',
            url: `${REACT_APP_API_URL}/v1/WorkTypes`,
            headers: {
                Authorization: idToken
            },
            data
        });

        // Set WorkType
        yield put(setWorkType(response.data));

        // Succeeded Create WorkType
        yield put(succeededCreateWorkType(response));

        // Update Query String
        yield put(
            updateQueryString({
                createWorkTypeModal: undefined
            })
        );
    } catch (error) {
        yield put(failedCreateWorkType(error));
    }
}

export function* watchWorkTypeCreateRequested(): Saga<void> {
    yield takeLatest(WORK_TYPE_CREATE_REQUESTED, createWorkType);
}

export function* deleteWorkType(action: Action): Saga<void> {
    const {
        payload: { id }
    } = action;

    const idToken = yield select(idTokenSelector);

    const config = {
        method: 'delete',
        url: `${REACT_APP_API_URL}/v1/WorkTypes/${id}`,
        headers: {
            Authorization: idToken
        }
    };

    try {
        const response = yield call(axios, config);

        // Set WorkType
        yield put(setWorkType(response.data));

        yield put(succeededDeleteWorkType(response));

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Task deleted successfully',
                options: { variant: 'success' }
            })
        );

        // Update Query String
        yield put(
            updateQueryString({
                workTypeDeleteModal: undefined,
                selectedWorkType: undefined
            })
        );
    } catch (error) {
        yield put(failedDeleteWorkType(error));
    }
}

export function* watchDeleteWorkType(): Saga<void> {
    yield takeLatest(WORK_TYPE_DELETE_REQUESTED, deleteWorkType);
}

export default function* workTypeSagas(): Saga<void> {
    yield all([fork(watchWorkTypeCreateRequested), fork(watchDeleteWorkType)]);
}
